<script lang="ts">import OtpInput from "./parts/input.svelte";
import Timer from "./parts/timer.svelte";
import { Button } from "@infinitro/button";
import { cfgToThemeCdnPath } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { closeThisModal, isSuccessStatus, loggedIn, ModalDialog, modalStack, stateUiTimerEnded, Status } from "@infinitro/global";
import { Modal } from "@infinitro/modal";
import { Text } from "@infinitro/text";
import { onMount } from "svelte";
import { derived } from "svelte/store";
import { stateError, handleCheckEmailVerifyCode as stateHandleCheckEmailVerifyCode, handleRequestEmailVerifyCode as stateHandleRequestEmailVerifyCode } from "./state";
console.log(`email_verify verify_email.svelte`);
const thisModal = ModalDialog.EmailVerify;
const modalIndex = derived(modalStack, ($modalStack) => $modalStack.findIndex((e) => e.modal === thisModal));
const isModalOpen = derived(modalIndex, ($modalIndex) => $modalIndex >= 0);
const zOverlay = derived(modalIndex, ($modalIndex) => $modalIndex >= 0 ? 9999 + $modalIndex * 2 : 9999);
const payload = derived(modalStack, ($modalStack) => $modalStack.find((e) => e.modal === thisModal)?.data);
const numInputs = derived(payload, ($payload) => $payload?.numInputs ?? 6);
let valid = undefined;
let submitting = false;
let fatalError = false;
const handleOtpInput = async (otpCode) => {
	return await handleCheckEmailVerifyCode($payload.email, otpCode, $loggedIn);
};
const handleCheckEmailVerifyCode = async (email, otpCode, isAuthenticated) => {
	try {
		const handler = $payload?.handleCheckEmailVerifyCode ?? stateHandleCheckEmailVerifyCode;
		submitting = true;
		const [pass, status, errorMessage, verifiedKey] = await handler(email, otpCode, isAuthenticated);
		submitting = false;
		valid = pass && status && isSuccessStatus(status);
		if (valid) {
			const handler = $payload?.handleConfirmedEmailVerifyCode;
			if (handler && typeof handler === "function") {
				setTimeout(async () => {
					handler(verifiedKey ?? "", email);
					closeThisModal(ModalDialog.EmailVerify);
				}, 300);
			}
		} else if (status === Status.TOO_MANY_ATTEMPTS) {
			fatalError = true;
			console.log(`fatal error ${valid}`);
			stateError.set($t(Verbiage.too_many_attempts_please_try_again_later));
		} else if (pass === false && status !== Status.MAINTENANCE) {
			stateError.set(errorMessage ?? status ?? "unknown error");
		}
		return [
			pass,
			status,
			errorMessage
		];
	} catch (e) {
		submitting = false;
		console.warn("error handling input complete", e);
		stateError.set(e);
		return [
			false,
			Status.FAILED,
			undefined
		];
	}
};
const closeModal = async () => {
	try {
		if ($payload?.onClose) {
			const pass = await $payload.onClose();
			if (pass === false) return;
		}
		closeThisModal(thisModal);
	} catch (e) {
		console.warn("error handling close", e);
		stateError.set(e);
	}
};
const handleRequestEmailVerifyCode = async (email, isAuthenticated) => {
	try {
		const handler = $payload?.handleRequestEmailVerifyCode ?? stateHandleRequestEmailVerifyCode;
		submitting = true;
		let [pass, status, errorMessage] = await handler(email, isAuthenticated);
		submitting = false;
		if (status === Status.TOO_MANY_ATTEMPTS) {
			fatalError = true;
			console.log(`fatal error`);
			stateError.set($t(Verbiage.too_many_attempts_please_try_again_later));
		} else if (pass === false && status !== Status.MAINTENANCE) {
			stateError.set(errorMessage ?? status ?? "unknown error");
		}
		return [
			pass,
			status,
			errorMessage
		];
	} catch (e) {
		submitting = false;
		console.warn("error handling resend", e);
		const err = e?.message ?? e?.toString() ?? "unknown error handling resend";
		stateError.set(err);
		return [
			false,
			Status.FAILED,
			err
		];
	}
};
onMount(() => {
	if ($payload?.requestOnOpen && $stateUiTimerEnded === true) {
		handleRequestEmailVerifyCode($payload?.email ?? "", $loggedIn);
	}
});
</script>

{#if $isModalOpen}
	<Modal
		{closeModal}
		title={$payload?.title ?? ''}
		zIndexOverlay={$zOverlay}
		zIndexUnderlay={$zOverlay - 1}
	>
		<div class="content">
			<img
				src={cfgToThemeCdnPath('/email-verification/illustration.png')}
				alt={$t(Verbiage.icon_alt, 'alts')}
			/>
			<p class="text">
				{`${$t(Verbiage.check_your_email_for_your_verification_code)}`}
			</p>

			<OtpInput
				numInputs={$numInputs}
				{handleOtpInput}
				label={$payload?.label ?? $payload?.email ?? ''}
				enabled={!fatalError}
			/>

			{#if $stateError}
				<Text
					type="body"
					bodySize="small"
					textColor="error"
					textAlign="center"
					customStyle="margin-top: 8px;"
				>
					{$stateError}
				</Text>
			{/if}

			<div class="flex">
				<Button
					text={`${$t(Verbiage.resend_code)}`}
					onClick={() => handleRequestEmailVerifyCode($payload?.email ?? '', $loggedIn)}
					fullWidth={true}
					disabled={fatalError || valid === true || submitting || $stateUiTimerEnded === false}
				/>
				{#if fatalError === false && $stateUiTimerEnded === false && !(valid === true)}
					<div class="timer-div">
						<Timer />
					</div>
				{/if}
			</div>
		</div>
	</Modal>
{/if}

<style>
	@media (min-width: 768px) {
		.modal {
				padding: 1.75rem
		}
	}
	.content {
		display: flex;
		width: 100%;
		flex-direction: column;
		justify-content: space-between
}
	.content > :not([hidden]) ~ :not([hidden]) {
		--tw-space-y-reverse: 0;
		margin-top: calc(0.75rem * calc(1 - var(--tw-space-y-reverse)));
		margin-bottom: calc(0.75rem * var(--tw-space-y-reverse))
}
	.content {
		padding: 0.25rem
}
	.text {
		padding-top: 1rem;
		padding-bottom: 1rem;
		text-align: center;
		font-size: 20px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}
	.timer-div {
		margin-left: 0.75rem;
		display: flex;
		justify-content: center
}
	.timer-div-no-margin {
		margin: 0px
}
	.content img {
		display: block;
		height: 130px;
		width: auto;
		align-self: center;
		transform: translateZ(0);
		backface-visibility: hidden;
		will-change: transform;
		image-rendering: auto
}
</style>
